import { queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  ClosedBusinessDay,
  CloseBusinessDayRequest,
  ClosePostingPeriodRequest,
  CreateGlAccountRequest,
  LedgerAuditEntry,
  LedgerAuditPage,
  LedgerBalanceDriftAlertRow,
  LedgerBusinessDayQuery,
  LedgerBusinessDayRow,
  LedgerChartOfAccounts,
  LedgerClassificationBand,
  LedgerClassificationScheme,
  LedgerDailyBalanceQuery,
  LedgerDriftAlertQuery,
  LedgerDriftCheckResult,
  LedgerGlAccountDetail,
  LedgerGlAccountNode,
  LedgerGlAccountQuery,
  LedgerGlAccountResult,
  LedgerGlDailyBalanceRow,
  LedgerJournalEntryDetail,
  LedgerJournalEntryRow,
  LedgerJournalLineRow,
  LedgerJournalQuery,
  LedgerPostingPeriodQuery,
  LedgerPostingPeriodResult,
  LedgerPostingPeriodRow,
  LedgerProvisionRunRequest,
  LedgerProvisionRunResult,
  LedgerReopenedBusinessDay,
  LedgerTrialBalanceQuery,
  LedgerTrialBalanceReport,
  LedgerTrialBalanceReturnLine,
  LedgerTrialBalanceRow,
  OpenBusinessDayRequest,
  OpenedBusinessDay,
  PostedJournalResult,
  PostManualJournalRequest,
  ReopenBusinessDayRequest,
  ReopenPostingPeriodRequest,
  ResolveDriftAlertRequest,
  ReverseJournalRequest,
  RunDriftCheckRequest,
  UpdateGlAccountRequest,
} from './ledger-types'
import type { PagedResult } from './types'

/**
 * The ledger endpoints — sixteen of them, which is the whole of what the general ledger publishes.
 *
 * Grouped the way the module is actually operated:
 *
 * - **The day.** `GET /ledger/business-day/current` reads a branch's trading day and carries no
 *   permission requirement on purpose, because the ledger date is the date every screen in the
 *   institution stamps its work with. It is already wrapped as `businessDayQueryOptions` in
 *   `reference-api.ts` for the application header, and the ledger screens re-use that query rather
 *   than issuing a second one: the header and this module must never disagree about whether a
 *   branch is trading. `GET /ledger/business-days` is the register behind it,
 *   `POST /ledger/business-days` opens one, and `POST /ledger/business-days/closure` closes one.
 * - **Journals.** List, read one with its lines, raise one by hand, reverse one. The last two are
 *   countersigned and answer with an envelope rather than with a journal.
 * - **The chart, the balances, and the trial balance.** All reads.
 * - **Periods.** List, close, reopen. Reopening is countersigned.
 * - **Drift.** The alarm register, an on-demand check, and the disposition of one alarm.
 *
 * Two reads from other modules are borrowed as context and are treated as borrowed: the
 * classification scheme list (`loans.view`) and the audit trail (`ops.audit.view`). Both are
 * requested only when the caller holds the permission, so a 403 is never mistaken for an empty book.
 *
 * **Nothing below trusts a payload's shape.** Every identifier is normalised to a string on the way
 * in and back to a number on the way out — see the note at the head of `ledger-types.ts` — every
 * `["integer","string"]` member is coerced through {@link toNumber}, and every array is checked for
 * being one before it is mapped. The ledger's shapes are wide (an `int64` a serialiser may send
 * quoted, a collection member the contract marks required) and a screen that assumed otherwise
 * white-screens in front of an accountant at month end.
 */

/**
 * Query keys for the area.
 *
 * `business-day` deliberately matches the key `reference-api.ts` already uses, so opening or
 * closing a day invalidates the header's copy of it in the same stroke as the ledger's.
 */
export const ledgerKeys = {
  all: ['ledger'] as const,
  businessDays: () => [...ledgerKeys.all, 'business-day'] as const,
  businessDay: (branchId: string | null) => [...ledgerKeys.businessDays(), branchId] as const,
  businessDayRegister: (query: LedgerBusinessDayQuery) =>
    [...ledgerKeys.all, 'business-day-register', query] as const,
  journals: () => [...ledgerKeys.all, 'journals'] as const,
  journalList: (query: LedgerJournalQuery) => [...ledgerKeys.journals(), 'list', query] as const,
  journal: (journalPublicId: string) => [...ledgerKeys.journals(), 'detail', journalPublicId] as const,
  glAccounts: () => [...ledgerKeys.all, 'gl-accounts'] as const,
  chartOfAccounts: (query: LedgerGlAccountQuery) => [...ledgerKeys.glAccounts(), 'chart', query] as const,
  glAccount: (code: string, branchId: string | null, asOf: string | null) =>
    [...ledgerKeys.glAccounts(), 'detail', code, branchId, asOf] as const,
  // The prefix and the specific key are both declared, because an invalidation after a posting has
  // to reach every range anyone has looked at rather than the one currently on screen.
  trialBalances: () => [...ledgerKeys.all, 'trial-balance'] as const,
  trialBalance: (query: LedgerTrialBalanceQuery) => [...ledgerKeys.trialBalances(), query] as const,
  dailyBalanceRollups: () => [...ledgerKeys.all, 'daily-balances'] as const,
  dailyBalances: (query: LedgerDailyBalanceQuery) => [...ledgerKeys.dailyBalanceRollups(), query] as const,
  postingPeriods: () => [...ledgerKeys.all, 'posting-periods'] as const,
  postingPeriodList: (query: LedgerPostingPeriodQuery) => [...ledgerKeys.postingPeriods(), query] as const,
  drift: () => [...ledgerKeys.all, 'balance-drift'] as const,
  driftAlerts: (query: LedgerDriftAlertQuery) => [...ledgerKeys.drift(), 'alerts', query] as const,
  classificationSchemes: () => [...ledgerKeys.all, 'classification-schemes'] as const,
  audit: (entityType: string, entityId: string, page: number) =>
    [...ledgerKeys.all, 'audit', entityType, entityId, page] as const,
}

/**
 * An `int64` or `int32` as the API serialises it.
 *
 * Numeric in practice; the schema types every one of them as `["integer","string"]`, which is a
 * serialiser's licence to send a large integer quoted. A client that read them as numbers alone
 * would get `NaN` the first day one arrived that way.
 */
type WireNumber = number | string

type WireId = WireNumber

const toId = (value: WireId): string => String(value)

const toNullableId = (value: WireId | null | undefined): string | null =>
  value === null || value === undefined ? null : String(value)

/**
 * A wire integer as a number.
 *
 * Used for counts and for money alike, and deliberately: a minor-unit amount is an `int64` the
 * contract types the same way. What this never does is *scale*. A pesewa in is the same pesewa out,
 * an exact integer, and it stays one until {@link MoneyDisplay} renders it — every magnitude the
 * ledger deals in is exact in double precision, and dividing by a hundred anywhere here is how a
 * trial balance comes out wrong by a factor of a hundred.
 */
const toNumber = (value: WireNumber): number => (typeof value === 'number' ? value : Number(value))

/** The same, with a fallback, for a member the server always sends and this client must not trust. */
const toCount = (value: WireNumber | null | undefined, fallback: number): number => {
  if (value === null || value === undefined) {
    return fallback
  }

  const parsed = toNumber(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

/** A collection member the contract marks required, read defensively. An absent one is empty. */
const toArray = <T>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])

/**
 * A branch identifier on the way out.
 *
 * The endpoints bind `long?`, and this application holds identifiers as strings. Null and empty
 * both mean "the caller's own branch", which is what the contract's nullable `branchId` means.
 */
const outboundBranchId = (branchId: string | null | undefined): number | null =>
  branchId === null || branchId === undefined || branchId === '' ? null : Number(branchId)

/**
 * Query-string parameters, with the members nobody chose removed.
 *
 * Axios serialises `undefined` away but sends `null` as an empty value, and an empty `status=`
 * is not the same request as no `status` at all. Dropping both here means an unfiltered screen
 * asks an unfiltered question.
 */
const params = (values: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  )

/** A page envelope, normalised. Counters fall back to what was asked for rather than to `NaN`. */
interface PagePayload<TRow> {
  items: TRow[] | null
  page: WireNumber | null
  pageSize: WireNumber | null
  totalCount: WireNumber | null
  totalPages: WireNumber | null
}

function toPage<TPayload, TRow>(
  payload: PagePayload<TPayload> | null | undefined,
  map: (row: TPayload) => TRow,
  requested: { page: number; pageSize: number },
): PagedResult<TRow> {
  const items = toArray(payload?.items).map(map)

  return {
    items,
    page: toCount(payload?.page, requested.page),
    pageSize: toCount(payload?.pageSize, requested.pageSize),
    totalCount: toCount(payload?.totalCount, items.length),
    totalPages: toCount(payload?.totalPages, 1),
  }
}

/* ─── Business day ─────────────────────────────────────────────────────────────────────────── */

interface OpenedBusinessDayPayload {
  branchId: WireId
  businessDate: string
  openedAtUtc: string
  periodCreated: boolean
}

/**
 * Opens a branch's trading day, which is what makes posting possible at all.
 *
 * Three outcomes are worth knowing before calling it, because the screens have to say which one
 * happened:
 *
 * - A day that is **already open** is answered successfully, with `periodCreated: false`. Two
 *   managers reaching for the same door at eight in the morning is not a fault.
 * - A day that has been **closed** is refused. Reopening one would change figures that have already
 *   been reported.
 * - A **future** date is refused.
 *
 * Opening also ensures the accounting period covering the date exists, and reports that through
 * `periodCreated`.
 */
export async function openBusinessDay(request: OpenBusinessDayRequest): Promise<OpenedBusinessDay> {
  const payload = (await apiClient.post<OpenedBusinessDayPayload>('/ledger/business-days', request)).data

  return {
    branchId: toId(payload.branchId),
    businessDate: payload.businessDate,
    openedAtUtc: payload.openedAtUtc,
    periodCreated: payload.periodCreated,
  }
}

interface BusinessDayRowPayload {
  branchId: WireId
  businessDate: string
  status: LedgerBusinessDayRow['status']
  openedAtUtc: string
  openedByUserId: WireId | null
  closedAtUtc: string | null
  closedByUserId: WireId | null
  carriedForwardNote: string | null
}

const toBusinessDayRow = (row: BusinessDayRowPayload): LedgerBusinessDayRow => ({
  branchId: toId(row.branchId),
  businessDate: row.businessDate,
  status: row.status,
  openedAtUtc: row.openedAtUtc,
  openedByUserId: toNullableId(row.openedByUserId),
  closedAtUtc: row.closedAtUtc,
  closedByUserId: toNullableId(row.closedByUserId),
  carriedForwardNote: row.carriedForwardNote,
})

/**
 * The register of trading days, newest first, across the branches the caller can reach.
 *
 * This is the history behind the current-day card: which days were opened, which were closed, by
 * whom, and what was carried forward. It is the read an auditor asks for and the read a branch
 * manager uses to find the day that was never closed.
 */
export const businessDayRegisterQueryOptions = (query: LedgerBusinessDayQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: ledgerKeys.businessDayRegister({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PagePayload<BusinessDayRowPayload>>('/ledger/business-days', {
          params: params({
            branchId: outboundBranchId(query.branchId),
            fromDate: query.fromDate,
            toDate: query.toDate,
            status: query.status,
            page,
            pageSize,
          }),
        })
      ).data

      return toPage(payload, toBusinessDayRow, { page, pageSize })
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

interface ClosedBusinessDayPayload {
  branchId: WireId
  businessDate: string
  status: ClosedBusinessDay['status']
  closedAtUtc: string | null
  carriedForwardNote: string | null
  carriedForward: string[] | null
  rollupsChecked: WireNumber
  rollupsRepaired: WireNumber
  awaitingApproval: boolean
  approvalRequestId: string | null
}

/**
 * Closes a branch's trading day.
 *
 * The control that turns a day's postings into a reported figure, and the one the ledger guards
 * hardest. It runs an integrity check on the way through — that is what `rollupsChecked` and
 * `rollupsRepaired` in the answer are — and it refuses outright while anything about the day is
 * unsettled: an unresolved drift alarm, an unbalanced till, work still awaiting a countersignature.
 * The refusal names its obstructions, and the close screen shows every one of them rather than
 * flashing a toast; see `close-business-day.tsx`, where that argument is made in full.
 *
 * The success path is **not** necessarily a closed day. `awaitingApproval` may come back true,
 * meaning the close is stored against an approval request and the branch is still trading.
 */
export async function closeBusinessDay(request: CloseBusinessDayRequest): Promise<ClosedBusinessDay> {
  const payload = (
    await apiClient.post<ClosedBusinessDayPayload>('/ledger/business-days/closure', request)
  ).data

  return {
    branchId: toId(payload.branchId),
    businessDate: payload.businessDate,
    status: payload.status,
    closedAtUtc: payload.closedAtUtc,
    carriedForwardNote: payload.carriedForwardNote,
    carriedForward: toArray(payload.carriedForward),
    rollupsChecked: toCount(payload.rollupsChecked, 0),
    rollupsRepaired: toCount(payload.rollupsRepaired, 0),
    awaitingApproval: payload.awaitingApproval,
    approvalRequestId: payload.approvalRequestId,
  }
}

interface ReopenedBusinessDayPayload {
  branchId: WireId
  businessDate: string
  status: LedgerReopenedBusinessDay['status']
  awaitingApproval: boolean
  approvalRequestId: string | null
}

/**
 * Reopens a branch's most recent closed day. **Countersigned** — `ledger.day.reopen.approve`.
 *
 * On a queued request the day comes back still Closed and `awaitingApproval` is the only member
 * that says what happened. The server fences the operation to the one safe case: the branch's
 * latest day, no newer day in any status, the accounting period still open.
 */
export async function reopenBusinessDay(
  request: ReopenBusinessDayRequest,
): Promise<LedgerReopenedBusinessDay> {
  const payload = (
    await apiClient.post<ReopenedBusinessDayPayload>('/ledger/business-days/reopening', request)
  ).data

  return {
    branchId: toId(payload.branchId),
    businessDate: payload.businessDate,
    status: payload.status,
    awaitingApproval: payload.awaitingApproval,
    approvalRequestId: payload.approvalRequestId,
  }
}

/* ─── Journals ─────────────────────────────────────────────────────────────────────────────── */

interface JournalEntryRowPayload {
  publicId: string
  entryNumber: string
  branchId: WireId
  businessDate: string
  valueDate: string
  postedAtUtc: string
  postedByUserId: WireId | null
  postedByUsername: string | null
  source: LedgerJournalEntryRow['source']
  status: LedgerJournalEntryRow['status']
  description: string
  isSystemGenerated: boolean
  isReversal: boolean
  isReversed: boolean
  totalDebitMinorUnits: WireNumber
  totalCreditMinorUnits: WireNumber
  currencyCode: string
  lineCount: WireNumber
}

const toJournalRow = (row: JournalEntryRowPayload): LedgerJournalEntryRow => ({
  publicId: row.publicId,
  entryNumber: row.entryNumber,
  branchId: toId(row.branchId),
  businessDate: row.businessDate,
  valueDate: row.valueDate,
  postedAtUtc: row.postedAtUtc,
  postedByUserId: toNullableId(row.postedByUserId),
  postedByUsername: row.postedByUsername,
  source: row.source,
  status: row.status,
  description: row.description,
  isSystemGenerated: row.isSystemGenerated,
  isReversal: row.isReversal,
  isReversed: row.isReversed,
  totalDebitMinorUnits: toNumber(row.totalDebitMinorUnits),
  totalCreditMinorUnits: toNumber(row.totalCreditMinorUnits),
  currencyCode: row.currencyCode,
  lineCount: toCount(row.lineCount, 0),
})

/** Journal entries within the caller's branch reach, newest first. */
export const journalListQueryOptions = (query: LedgerJournalQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: ledgerKeys.journalList({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PagePayload<JournalEntryRowPayload>>('/ledger/journals', {
          params: params({
            branchId: outboundBranchId(query.branchId),
            fromDate: query.fromDate,
            toDate: query.toDate,
            // Sent only when true: the endpoint defaults to the business date, and an explicit
            // `onValueDate=false` is the same request with more noise in the log.
            onValueDate: query.onValueDate === true ? true : undefined,
            source: query.source,
            status: query.status,
            glAccountCode: query.glAccountCode,
            reference: query.reference,
            page,
            pageSize,
          }),
        })
      ).data

      return toPage(payload, toJournalRow, { page, pageSize })
    },
    enabled,
    // A posted journal never changes, but the list it appears in gains rows continuously while a
    // branch is trading. Short rather than zero: this screen is scrolled, not watched.
    staleTime: 15 * 1000,
  })
}

interface JournalLineRowPayload {
  lineNumber: WireNumber
  glAccountCode: string
  glAccountName: string
  branchId: WireId
  debitMinorUnits: WireNumber
  creditMinorUnits: WireNumber
  currencyCode: string
  narration: string | null
  customerId: WireId | null
  savingsAccountId: WireId | null
  loanCycleId: WireId | null
  tillId: WireId | null
  loanProductId: WireId | null
  loanOfficerUserId: WireId | null
}

const toJournalLine = (line: JournalLineRowPayload): LedgerJournalLineRow => ({
  lineNumber: toCount(line.lineNumber, 0),
  glAccountCode: line.glAccountCode,
  glAccountName: line.glAccountName,
  branchId: toId(line.branchId),
  debitMinorUnits: toNumber(line.debitMinorUnits),
  creditMinorUnits: toNumber(line.creditMinorUnits),
  currencyCode: line.currencyCode,
  narration: line.narration,
  customerId: toNullableId(line.customerId),
  savingsAccountId: toNullableId(line.savingsAccountId),
  loanCycleId: toNullableId(line.loanCycleId),
  tillId: toNullableId(line.tillId),
  loanProductId: toNullableId(line.loanProductId),
  loanOfficerUserId: toNullableId(line.loanOfficerUserId),
})

interface JournalEntryDetailPayload extends Omit<JournalEntryRowPayload, 'lineCount'> {
  reversalReason: string | null
  reversalOfEntryPublicId: string | null
  reversedByEntryPublicId: string | null
  financialTransactionId: string | null
  correlationId: string | null
  lines: JournalLineRowPayload[] | null
}

/**
 * One entry with every line on it.
 *
 * Cached indefinitely and deliberately: a posted journal is immutable, so re-reading one can only
 * ever produce the same answer. The two members that *can* change are `status` and
 * `reversedByEntryPublicId`, and both change only when this application reverses the entry — which
 * invalidates this key explicitly rather than polling for it.
 */
export const journalQueryOptions = (journalPublicId: string, enabled = true) =>
  queryOptions({
    queryKey: ledgerKeys.journal(journalPublicId),
    queryFn: async () => {
      const payload = (
        await apiClient.get<JournalEntryDetailPayload>(
          `/ledger/journals/${encodeURIComponent(journalPublicId)}`,
        )
      ).data

      return {
        publicId: payload.publicId,
        entryNumber: payload.entryNumber,
        branchId: toId(payload.branchId),
        businessDate: payload.businessDate,
        valueDate: payload.valueDate,
        postedAtUtc: payload.postedAtUtc,
        postedByUserId: toNullableId(payload.postedByUserId),
        postedByUsername: payload.postedByUsername,
        source: payload.source,
        status: payload.status,
        description: payload.description,
        isSystemGenerated: payload.isSystemGenerated,
        isReversal: payload.isReversal,
        isReversed: payload.isReversed,
        reversalReason: payload.reversalReason,
        reversalOfEntryPublicId: payload.reversalOfEntryPublicId,
        reversedByEntryPublicId: payload.reversedByEntryPublicId,
        financialTransactionId: payload.financialTransactionId,
        correlationId: payload.correlationId,
        totalDebitMinorUnits: toNumber(payload.totalDebitMinorUnits),
        totalCreditMinorUnits: toNumber(payload.totalCreditMinorUnits),
        currencyCode: payload.currencyCode,
        lines: toArray(payload.lines).map(toJournalLine),
      } satisfies LedgerJournalEntryDetail
    },
    enabled: enabled && journalPublicId !== '',
    staleTime: Number.POSITIVE_INFINITY,
  })

interface PostedJournalResultPayload {
  awaitingApproval: boolean
  approvalRequestId: string | null
  journalPublicId: string | null
  entryNumber: string | null
  businessDate: string | null
}

const toPostedJournalResult = (payload: PostedJournalResultPayload): PostedJournalResult => ({
  awaitingApproval: payload.awaitingApproval,
  approvalRequestId: payload.approvalRequestId,
  journalPublicId: payload.journalPublicId,
  entryNumber: payload.entryNumber,
  businessDate: payload.businessDate,
})

/**
 * Raises a journal by hand. **Countersigned.**
 *
 * The answer is an envelope, not a journal. `awaitingApproval: true` means the request is stored
 * and nothing has posted — the entry number, the public id and the business date are all null on
 * that path, which is why every one of them is nullable in the type. The caller reports the outcome
 * through `LedgerDualControlOutcome` and never assumes the absence of an error meant a posting.
 */
export async function postManualJournal(request: PostManualJournalRequest): Promise<PostedJournalResult> {
  const payload = (await apiClient.post<PostedJournalResultPayload>('/ledger/journals', request)).data
  return toPostedJournalResult(payload)
}

/**
 * Reverses a posted entry into the current open day. **Countersigned.**
 *
 * This is the *only* correction the ledger offers. A posted entry is never edited and never
 * deleted: the reversal is a second entry that undoes the first, both stay in the book, and the
 * pair is what an auditor reads. The reason is mandatory and is carried on both.
 */
export async function reverseJournal(
  journalPublicId: string,
  request: ReverseJournalRequest,
): Promise<PostedJournalResult> {
  const payload = (
    await apiClient.post<PostedJournalResultPayload>(
      `/ledger/journals/${encodeURIComponent(journalPublicId)}/reversal`,
      request,
    )
  ).data

  return toPostedJournalResult(payload)
}

/* ─── Chart of accounts ────────────────────────────────────────────────────────────────────── */

interface GlAccountNodePayload {
  publicId: string
  code: string
  name: string
  class: LedgerGlAccountNode['class']
  normalBalance: LedgerGlAccountNode['normalBalance']
  isPostable: boolean
  isSystemAccount: boolean
  status: LedgerGlAccountNode['status']
  currencyCode: string
  boGReturnLineCode: string | null
  description: string | null
  children: GlAccountNodePayload[] | null
}

/** Recursive by nature: the chart is a tree and its depth is the institution's business. */
const toAccountNode = (node: GlAccountNodePayload): LedgerGlAccountNode => ({
  publicId: node.publicId,
  code: node.code,
  name: node.name,
  class: node.class,
  normalBalance: node.normalBalance,
  isPostable: node.isPostable,
  isSystemAccount: node.isSystemAccount,
  status: node.status,
  currencyCode: node.currencyCode,
  boGReturnLineCode: node.boGReturnLineCode,
  description: node.description,
  children: toArray(node.children).map(toAccountNode),
})

interface ChartOfAccountsPayload {
  roots: GlAccountNodePayload[] | null
  accountCount: WireNumber
  postableCount: WireNumber
}

/**
 * The chart of accounts, as a tree.
 *
 * Institution-wide rather than branch-scoped, and long-lived: the chart changes only under the
 * countersigned management operations below, so an hour is a conservative staleness — and the
 * mutations invalidate it anyway.
 */
export const chartOfAccountsQueryOptions = (query: LedgerGlAccountQuery, enabled = true) =>
  queryOptions({
    queryKey: ledgerKeys.chartOfAccounts(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ChartOfAccountsPayload>('/ledger/gl-accounts', {
          params: params({
            search: query.search,
            accountClass: query.accountClass,
            postableOnly: query.postableOnly === true ? true : undefined,
            includeArchived: query.includeArchived === true ? true : undefined,
          }),
        })
      ).data

      return {
        roots: toArray(payload?.roots).map(toAccountNode),
        accountCount: toCount(payload?.accountCount, 0),
        postableCount: toCount(payload?.postableCount, 0),
      } satisfies LedgerChartOfAccounts
    },
    enabled,
    staleTime: 60 * 60 * 1000,
  })

interface GlAccountResultPayload {
  awaitingApproval: boolean
  approvalRequestId: string | null
  account: GlAccountNodePayload | null
}

const toGlAccountResult = (payload: GlAccountResultPayload): LedgerGlAccountResult => ({
  awaitingApproval: payload.awaitingApproval,
  approvalRequestId: payload.approvalRequestId,
  account: payload.account ? toAccountNode(payload.account) : null,
})

/**
 * Adds an account to the chart. **Countersigned** — `ledger.account.manage.approve`.
 *
 * On a queued request `account` is null and nothing exists yet: a screen that rendered the row it
 * asked to create would show a chart entry the ledger does not have.
 */
export async function createGlAccount(request: CreateGlAccountRequest): Promise<LedgerGlAccountResult> {
  const payload = (await apiClient.post<GlAccountResultPayload>('/ledger/gl-accounts', request)).data
  return toGlAccountResult(payload)
}

/**
 * Renames, remaps, blocks, unblocks or archives an account. **Countersigned** — same code.
 *
 * The request carries the account's full mutable state, so what the checker approves is what the
 * account will be. Structure (code, class, parent, postability) cannot change — a
 * differently-shaped account is a new account.
 */
export async function updateGlAccount(
  code: string,
  request: UpdateGlAccountRequest,
): Promise<LedgerGlAccountResult> {
  const payload = (
    await apiClient.put<GlAccountResultPayload>(`/ledger/gl-accounts/${encodeURIComponent(code)}`, request)
  ).data

  return toGlAccountResult(payload)
}

interface GlAccountDetailPayload {
  account: GlAccountNodePayload
  ancestorCodes: string[] | null
  branchId: WireId | null
  asOf: string
  balanceMinorUnits: WireNumber | null
}

/**
 * One account, its subtree, its path to the root, and its balance as at a date.
 *
 * `balanceMinorUnits` is nullable and null is not zero: a heading holds no balance, and neither
 * does a postable account that has never been posted to. The screen renders the null as an em dash
 * rather than as a figure the ledger never asserted.
 */
export const glAccountQueryOptions = (
  code: string,
  options: { branchId?: string | null; asOf?: string | null } = {},
  enabled = true,
) => {
  const branchId = options.branchId ?? null
  const asOf = options.asOf ?? null

  return queryOptions({
    queryKey: ledgerKeys.glAccount(code, branchId, asOf),
    queryFn: async () => {
      const payload = (
        await apiClient.get<GlAccountDetailPayload>(`/ledger/gl-accounts/${encodeURIComponent(code)}`, {
          params: params({ branchId: outboundBranchId(branchId), asOf }),
        })
      ).data

      return {
        account: toAccountNode(payload.account),
        ancestorCodes: toArray(payload.ancestorCodes),
        branchId: toNullableId(payload.branchId),
        asOf: payload.asOf,
        balanceMinorUnits:
          payload.balanceMinorUnits === null || payload.balanceMinorUnits === undefined
            ? null
            : toNumber(payload.balanceMinorUnits),
      } satisfies LedgerGlAccountDetail
    },
    enabled: enabled && code !== '',
    staleTime: 5 * 60 * 1000,
  })
}

/* ─── Trial balance ────────────────────────────────────────────────────────────────────────── */

interface TrialBalanceRowPayload {
  glAccountCode: string
  name: string
  class: LedgerTrialBalanceRow['class']
  normalBalance: LedgerTrialBalanceRow['normalBalance']
  boGReturnLineCode: string | null
  openingMinorUnits: WireNumber
  debitMinorUnits: WireNumber
  creditMinorUnits: WireNumber
  closingMinorUnits: WireNumber
  closingDebitMinorUnits: WireNumber
  closingCreditMinorUnits: WireNumber
}

interface TrialBalanceReturnLinePayload {
  boGReturnLineCode: string
  accountCount: WireNumber
  closingDebitMinorUnits: WireNumber
  closingCreditMinorUnits: WireNumber
}

interface TrialBalanceReportPayload {
  fromDate: string
  toDate: string
  branchId: WireId | null
  currencyCode: string
  rows: TrialBalanceRowPayload[] | null
  totalDebitMinorUnits: WireNumber
  totalCreditMinorUnits: WireNumber
  totalClosingDebitMinorUnits: WireNumber
  totalClosingCreditMinorUnits: WireNumber
  movementBalances: boolean
  positionBalances: boolean
  movementDifferenceMinorUnits: WireNumber
  positionDifferenceMinorUnits: WireNumber
  returnLines: TrialBalanceReturnLinePayload[] | null
  untaggedAccountCount: WireNumber
  generatedAtUtc: string
}

/**
 * The trial balance for a range and a branch.
 *
 * The two `*Balances` booleans are the server's own verdict and the report's most important output.
 * They are read as sent and never re-derived here: summing a page of rows in the browser would be a
 * second opinion computed from less information than the server had, and where the two disagreed
 * nobody could say which was right.
 *
 * `staleTime: 0` because this is a report somebody is about to act on. A trial balance held over
 * from five minutes ago is a trial balance from before the postings that were just made.
 */
export const trialBalanceQueryOptions = (query: LedgerTrialBalanceQuery, enabled = true) =>
  queryOptions({
    queryKey: ledgerKeys.trialBalance(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<TrialBalanceReportPayload>('/ledger/trial-balance', {
          params: params({
            fromDate: query.fromDate,
            toDate: query.toDate,
            branchId: outboundBranchId(query.branchId),
            includeZeroBalances: query.includeZeroBalances === true ? true : undefined,
          }),
        })
      ).data

      return {
        fromDate: payload.fromDate,
        toDate: payload.toDate,
        branchId: toNullableId(payload.branchId),
        currencyCode: payload.currencyCode,
        rows: toArray(payload.rows).map(
          (row): LedgerTrialBalanceRow => ({
            glAccountCode: row.glAccountCode,
            name: row.name,
            class: row.class,
            normalBalance: row.normalBalance,
            boGReturnLineCode: row.boGReturnLineCode,
            openingMinorUnits: toNumber(row.openingMinorUnits),
            debitMinorUnits: toNumber(row.debitMinorUnits),
            creditMinorUnits: toNumber(row.creditMinorUnits),
            closingMinorUnits: toNumber(row.closingMinorUnits),
            closingDebitMinorUnits: toNumber(row.closingDebitMinorUnits),
            closingCreditMinorUnits: toNumber(row.closingCreditMinorUnits),
          }),
        ),
        totalDebitMinorUnits: toNumber(payload.totalDebitMinorUnits),
        totalCreditMinorUnits: toNumber(payload.totalCreditMinorUnits),
        totalClosingDebitMinorUnits: toNumber(payload.totalClosingDebitMinorUnits),
        totalClosingCreditMinorUnits: toNumber(payload.totalClosingCreditMinorUnits),
        movementBalances: payload.movementBalances,
        positionBalances: payload.positionBalances,
        movementDifferenceMinorUnits: toNumber(payload.movementDifferenceMinorUnits),
        positionDifferenceMinorUnits: toNumber(payload.positionDifferenceMinorUnits),
        returnLines: toArray(payload.returnLines).map(
          (line): LedgerTrialBalanceReturnLine => ({
            boGReturnLineCode: line.boGReturnLineCode,
            accountCount: toCount(line.accountCount, 0),
            closingDebitMinorUnits: toNumber(line.closingDebitMinorUnits),
            closingCreditMinorUnits: toNumber(line.closingCreditMinorUnits),
          }),
        ),
        untaggedAccountCount: toCount(payload.untaggedAccountCount, 0),
        generatedAtUtc: payload.generatedAtUtc,
      } satisfies LedgerTrialBalanceReport
    },
    enabled: enabled && query.fromDate !== '' && query.toDate !== '',
    staleTime: 0,
  })

/* ─── Daily balances ───────────────────────────────────────────────────────────────────────── */

interface GlDailyBalanceRowPayload {
  glAccountCode: string
  glAccountName: string
  branchId: WireId
  businessDate: string
  currencyCode: string
  openingMinorUnits: WireNumber
  debitMinorUnits: WireNumber
  creditMinorUnits: WireNumber
  closingMinorUnits: WireNumber
  lastUpdatedAtUtc: string
}

/** The stored rollups a trial balance is built from, and a drift check re-derives. */
export const dailyBalancesQueryOptions = (query: LedgerDailyBalanceQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 100

  return queryOptions({
    queryKey: ledgerKeys.dailyBalances({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PagePayload<GlDailyBalanceRowPayload>>('/ledger/daily-balances', {
          params: params({
            fromDate: query.fromDate,
            toDate: query.toDate,
            glAccountCode: query.glAccountCode,
            branchId: outboundBranchId(query.branchId),
            page,
            pageSize,
          }),
        })
      ).data

      return toPage(
        payload,
        (row): LedgerGlDailyBalanceRow => ({
          glAccountCode: row.glAccountCode,
          glAccountName: row.glAccountName,
          branchId: toId(row.branchId),
          businessDate: row.businessDate,
          currencyCode: row.currencyCode,
          openingMinorUnits: toNumber(row.openingMinorUnits),
          debitMinorUnits: toNumber(row.debitMinorUnits),
          creditMinorUnits: toNumber(row.creditMinorUnits),
          closingMinorUnits: toNumber(row.closingMinorUnits),
          lastUpdatedAtUtc: row.lastUpdatedAtUtc,
        }),
        { page, pageSize },
      )
    },
    enabled: enabled && query.fromDate !== '' && query.toDate !== '',
    staleTime: 30 * 1000,
  })
}

/* ─── Posting periods ──────────────────────────────────────────────────────────────────────── */

interface PostingPeriodRowPayload {
  year: WireNumber
  month: WireNumber
  status: LedgerPostingPeriodRow['status']
  startDate: string
  endDate: string
  softClosedAtUtc: string | null
  hardClosedAtUtc: string | null
  closedByUserId: WireId | null
}

const toPostingPeriod = (row: PostingPeriodRowPayload): LedgerPostingPeriodRow => ({
  year: toCount(row.year, 0),
  month: toCount(row.month, 0),
  status: row.status,
  startDate: row.startDate,
  endDate: row.endDate,
  softClosedAtUtc: row.softClosedAtUtc,
  hardClosedAtUtc: row.hardClosedAtUtc,
  closedByUserId: toNullableId(row.closedByUserId),
})

/**
 * The accounting periods and their close state, newest first.
 *
 * A bare array rather than a page — the endpoint takes `take` and returns that many, which is the
 * right shape for a list whose whole useful extent is three years of months.
 */
export const postingPeriodsQueryOptions = (query: LedgerPostingPeriodQuery, enabled = true) =>
  queryOptions({
    queryKey: ledgerKeys.postingPeriodList(query),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PostingPeriodRowPayload[]>('/ledger/posting-periods', {
          params: params({ year: query.year, status: query.status, take: query.take }),
        })
      ).data

      return toArray(payload).map(toPostingPeriod)
    },
    enabled,
    staleTime: 60 * 1000,
  })

interface PostingPeriodResultPayload {
  period: PostingPeriodRowPayload
  awaitingApproval: boolean
  approvalRequestId: string | null
}

const toPostingPeriodResult = (payload: PostingPeriodResultPayload): LedgerPostingPeriodResult => ({
  period: toPostingPeriod(payload.period),
  awaitingApproval: payload.awaitingApproval,
  approvalRequestId: payload.approvalRequestId,
})

/**
 * Soft closes a period, or seals it.
 *
 * Not countersigned in the catalogue — `ledger.period.close` has no `.approve` sibling — but the
 * response carries the dual-control envelope anyway, because an institution's approval policy is a
 * configurable row and may require one. The screens read `awaitingApproval` rather than assuming.
 */
export async function closePostingPeriod(
  request: ClosePostingPeriodRequest,
): Promise<LedgerPostingPeriodResult> {
  const payload = (
    await apiClient.post<PostingPeriodResultPayload>('/ledger/posting-periods/closure', request)
  ).data

  return toPostingPeriodResult(payload)
}

/**
 * Reopens a closed period. **Countersigned** — `ledger.period.reopen.approve`.
 *
 * On a queued request `period` comes back *unchanged*, still closed, and `awaitingApproval` is the
 * only member that says what happened. A screen reading the period's status alone would report the
 * reopening as having silently failed.
 */
export async function reopenPostingPeriod(
  request: ReopenPostingPeriodRequest,
): Promise<LedgerPostingPeriodResult> {
  const payload = (
    await apiClient.post<PostingPeriodResultPayload>('/ledger/posting-periods/reopening', request)
  ).data

  return toPostingPeriodResult(payload)
}

/* ─── Balance drift ────────────────────────────────────────────────────────────────────────── */

interface BalanceDriftAlertRowPayload {
  publicId: string
  detectedAtUtc: string
  subject: string
  subjectKey: string
  branchId: WireId | null
  businessDate: string | null
  storedMinorUnits: WireNumber
  derivedMinorUnits: WireNumber
  differenceMinorUnits: WireNumber
  resolution: LedgerBalanceDriftAlertRow['resolution']
  resolvedAtUtc: string | null
  resolvedByUserId: WireId | null
  resolutionNote: string | null
}

const toDriftAlert = (row: BalanceDriftAlertRowPayload): LedgerBalanceDriftAlertRow => ({
  publicId: row.publicId,
  detectedAtUtc: row.detectedAtUtc,
  subject: row.subject,
  subjectKey: row.subjectKey,
  branchId: toNullableId(row.branchId),
  businessDate: row.businessDate,
  storedMinorUnits: toNumber(row.storedMinorUnits),
  derivedMinorUnits: toNumber(row.derivedMinorUnits),
  differenceMinorUnits: toNumber(row.differenceMinorUnits),
  resolution: row.resolution,
  resolvedAtUtc: row.resolvedAtUtc,
  resolvedByUserId: toNullableId(row.resolvedByUserId),
  resolutionNote: row.resolutionNote,
})

/** The drift register, unresolved first — which is the order the work has to be done in. */
export const driftAlertsQueryOptions = (query: LedgerDriftAlertQuery, enabled = true) => {
  const page = query.page ?? 1
  const pageSize = query.pageSize ?? 50

  return queryOptions({
    queryKey: ledgerKeys.driftAlerts({ ...query, page, pageSize }),
    queryFn: async () => {
      const payload = (
        await apiClient.get<PagePayload<BalanceDriftAlertRowPayload>>('/ledger/balance-drift/alerts', {
          params: params({
            branchId: outboundBranchId(query.branchId),
            fromDate: query.fromDate,
            toDate: query.toDate,
            resolution: query.resolution,
            page,
            pageSize,
          }),
        })
      ).data

      return toPage(payload, toDriftAlert, { page, pageSize })
    },
    enabled,
    staleTime: 30 * 1000,
  })
}

/**
 * Re-derives the daily rollups from the postings and alarms on any that disagree.
 *
 * Safe to repeat and expensive to run: it reads every posting in the range. It is also what a day
 * close performs on the way through, which is why running it by hand before a close is how a branch
 * finds out what would block one.
 */
export async function runDriftCheck(request: RunDriftCheckRequest): Promise<LedgerDriftCheckResult> {
  const payload = (
    await apiClient.post<{
      runPublicId: string
      trigger: LedgerDriftCheckResult['trigger']
      branchId: WireId | null
      fromDate: string
      toDate: string
      rollupsChecked: WireNumber
      rollupsDrifted: WireNumber
      rollupsRepaired: WireNumber
      rollupsMissing: WireNumber
      startedAtUtc: string
      completedAtUtc: string
      alerts: BalanceDriftAlertRowPayload[] | null
    }>('/ledger/balance-drift/checks', request)
  ).data

  return {
    runPublicId: payload.runPublicId,
    trigger: payload.trigger,
    branchId: toNullableId(payload.branchId),
    fromDate: payload.fromDate,
    toDate: payload.toDate,
    rollupsChecked: toCount(payload.rollupsChecked, 0),
    rollupsDrifted: toCount(payload.rollupsDrifted, 0),
    rollupsRepaired: toCount(payload.rollupsRepaired, 0),
    rollupsMissing: toCount(payload.rollupsMissing, 0),
    startedAtUtc: payload.startedAtUtc,
    completedAtUtc: payload.completedAtUtc,
    alerts: toArray(payload.alerts).map(toDriftAlert),
  }
}

/**
 * Disposes of one alarm so the branch can close its books.
 *
 * Not countersigned — the answer is the alarm itself, in its new state, rather than an envelope.
 * The control here is the mandatory note, which is the whole of what an `AcceptedWithExplanation`
 * disposition amounts to: somebody's name against a figure the machine could not reconcile.
 */
export async function resolveDriftAlert(
  alertId: string,
  request: ResolveDriftAlertRequest,
): Promise<LedgerBalanceDriftAlertRow> {
  const payload = (
    await apiClient.post<BalanceDriftAlertRowPayload>(
      `/ledger/balance-drift/alerts/${encodeURIComponent(alertId)}/resolution`,
      request,
    )
  ).data

  return toDriftAlert(payload)
}

/* ─── Provisioning ─────────────────────────────────────────────────────────────────────────── */

interface ProvisionRunPayload {
  branchId: WireId
  asOf: string
  schemeConfigured: boolean
  schemeIsProvisional: boolean
  classified: WireNumber
  unclassified: WireNumber
  provisionRequiredMinor: WireNumber
  provisionMovementMinor: WireNumber
  currencyCode: string
  journalEntry: string | null
}

/**
 * Classifies a branch's live rounds as at a date and posts the movement in the loan-loss allowance.
 *
 * A posting into the general ledger that is not countersigned: it executes on the click. It is also
 * safe to repeat — the movement is measured against the allowance already carried, so a second run
 * on the same day posts nothing and answers with a null `journalEntry`.
 *
 * With no scheme in force for `asOf`, nothing is classified, nothing is posted, and
 * `schemeConfigured` is false while `unclassified` carries the count of rounds that went unjudged.
 * That pair is the whole reason this response is not a bare success.
 */
export async function runProvisionClassification(
  request: LedgerProvisionRunRequest,
): Promise<LedgerProvisionRunResult> {
  const payload = (await apiClient.post<ProvisionRunPayload>('/loans/classification-runs', request)).data

  return {
    branchId: toId(payload.branchId),
    asOf: payload.asOf,
    schemeConfigured: payload.schemeConfigured,
    schemeIsProvisional: payload.schemeIsProvisional,
    classified: toNumber(payload.classified),
    unclassified: toNumber(payload.unclassified),
    provisionRequiredMinor: toNumber(payload.provisionRequiredMinor),
    provisionMovementMinor: toNumber(payload.provisionMovementMinor),
    currencyCode: payload.currencyCode,
    journalEntry: payload.journalEntry,
  }
}

/* ─── Classification schemes (borrowed from loans) ─────────────────────────────────────────── */

interface ClassificationBandPayload {
  classCode: string
  className: string
  minDaysOverdue: WireNumber
  maxDaysOverdue: WireNumber | null
  provisionRate: WireNumber
  suspendsInterestAccrual: boolean
  isPerforming: boolean
}

interface ClassificationSchemePayload {
  id: string
  code: string
  name: string
  effectiveFrom: string
  isProvisional: boolean
  sourceReference: string | null
  provisionBasis: LedgerClassificationScheme['provisionBasis']
  restructuredFloorClassCode: string | null
  restructuredCoolingDays: WireNumber | null
  bands: ClassificationBandPayload[]
}

const toBand = (band: ClassificationBandPayload): LedgerClassificationBand => ({
  classCode: band.classCode,
  className: band.className,
  minDaysOverdue: toNumber(band.minDaysOverdue),
  maxDaysOverdue: band.maxDaysOverdue === null ? null : toNumber(band.maxDaysOverdue),
  provisionRate: toNumber(band.provisionRate),
  suspendsInterestAccrual: band.suspendsInterestAccrual,
  isPerforming: band.isPerforming,
})

/**
 * The classification schemes and the dates they take effect, newest first.
 *
 * Borrowed from the loans module and gated there on `loans.view`, which a ledger-only role need not
 * hold — hence `enabled`, which the caller sets from the permission rather than from whether the
 * screen happens to be open. An empty array is the honest answer on a system where the Bank of
 * Ghana figures have never been loaded, and it is a different fact from a refused read.
 */
export const classificationSchemesQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: ledgerKeys.classificationSchemes(),
    queryFn: async () => {
      const payload = (
        await apiClient.get<ClassificationSchemePayload[]>('/loan-classification-schemes')
      ).data

      return toArray(payload).map(
        (scheme): LedgerClassificationScheme => ({
          id: scheme.id,
          code: scheme.code,
          name: scheme.name,
          effectiveFrom: scheme.effectiveFrom,
          isProvisional: scheme.isProvisional,
          sourceReference: scheme.sourceReference,
          provisionBasis: scheme.provisionBasis,
          restructuredFloorClassCode: scheme.restructuredFloorClassCode,
          restructuredCoolingDays:
            scheme.restructuredCoolingDays === null ? null : toNumber(scheme.restructuredCoolingDays),
          bands: toArray(scheme.bands).map(toBand),
        }),
      )
    },
    enabled,
    // A scheme comes into force on a dated instrument and is created under dual control; it does
    // not change while somebody is looking at a run.
    staleTime: 30 * 60 * 1000,
  })

/* ─── Audit trail (borrowed from access) ───────────────────────────────────────────────────── */

interface AuditEntryPayload {
  id: WireId
  occurredAtUtc: string
  actionCode: string
  entityType: string
  entityId: string | null
  actorUserId: WireId | null
  actorUsername: string
  approverUserId: WireId | null
  approverUsername: string | null
  branchId: WireId | null
  source: number
  outcome: number
  failureReason: string | null
  correlationId: string
  beforeJson: string | null
  afterJson: string | null
}

interface AuditPagePayload {
  items: AuditEntryPayload[]
  page: WireNumber
  pageSize: WireNumber
  totalCount: WireNumber
}

export interface LedgerAuditQuery {
  /** Entity name as the server records it — `BusinessDay`, `JournalEntry`, `PostingPeriod`. */
  entityType: string
  /** The identifier that entity was audited under. For a business day, `{branchId}:{date}`. */
  entityId: string
  page?: number
  pageSize?: number
}

/**
 * The audit trail for one entity, newest first.
 *
 * With the journal endpoints in place this is no longer the ledger's only evidence path, but it is
 * still the only record of *who* acted and whether a countersignature was involved — a journal row
 * carries `postedByUserId` and nothing about an approver.
 *
 * It reads `GET /access/audit/{entityType}/{entityId}`, the path the contract publishes, rather than
 * the `/access/audit-events` collection `audit-api.ts` reaches for, which the contract does not
 * carry. Both path segments can be user-supplied, so both are encoded. Reading a trail is itself an
 * audited action, so this never fires speculatively: `enabled` is the caller's.
 */
export const ledgerAuditTrailQueryOptions = (
  { entityType, entityId, page = 1, pageSize = 25 }: LedgerAuditQuery,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: ledgerKeys.audit(entityType, entityId, page),
    queryFn: async () => {
      const payload = (
        await apiClient.get<AuditPagePayload>(
          `/access/audit/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
          { params: { page, pageSize } },
        )
      ).data

      const items = toArray(payload?.items)

      return {
        items: items.map(
          (entry): LedgerAuditEntry => ({
            id: toId(entry.id),
            occurredAtUtc: entry.occurredAtUtc,
            actionCode: entry.actionCode,
            entityType: entry.entityType,
            entityId: entry.entityId,
            actorUserId: toNullableId(entry.actorUserId),
            actorUsername: entry.actorUsername,
            approverUserId: toNullableId(entry.approverUserId),
            approverUsername: entry.approverUsername,
            branchId: toNullableId(entry.branchId),
            source: entry.source,
            outcome: entry.outcome,
            failureReason: entry.failureReason,
            correlationId: entry.correlationId,
            beforeJson: entry.beforeJson,
            afterJson: entry.afterJson,
          }),
        ),
        /*
         * The counters are guarded the same way `items` is, and for the same reason. The contract
         * marks all three required, so a missing one means the answer was not the shape it claims —
         * and `Number(undefined)` is `NaN`, which would surface on the enquiry screen as "page 1 of
         * NaN" beside a real audit record. The request's own paging is the honest fallback: it is
         * what was asked for.
         */
        page: toCount(payload?.page, page),
        pageSize: toCount(payload?.pageSize, pageSize),
        totalCount: toCount(payload?.totalCount, items.length),
      } satisfies LedgerAuditPage
    },
    enabled,
    staleTime: 0,
  })
